import { supabase } from '../lib/supabase'
import { slugify } from './links'
import { allDays, blocksFromAnswers, piecesFrom, starterPlan, type SetupAnswers } from './clientSetup'

/**
 * Everything the setup flow decided, written onto the project the context
 * has just created.
 *
 * **A tick is never left empty.** Ticking the shoot dates writes the shoot
 * days; ticking the tracker writes a starter one. There are only two ticks
 * on that screen — see the note on DURING in utils/clientSetup.ts for why
 * the call sheet is no longer the third.
 *
 * **NOTHING HERE MAKES A CALL SHEET (17 Oct 2026).** For one day it did:
 * the dead `callsheet` tick was wired up to create one empty sheet per
 * shoot day. That fixed the wrong half. A call sheet is not part of a
 * client page at all — Tom: *"The call sheet is generated from that tab
 * and a link is sent directly to either a whatsapp group or emailed
 * directly. It is different from the client page."* Making them here as
 * well would have left two places in the OS that create call sheets,
 * which is the duplication the workflow audit went looking for.
 *
 * What this file does for call sheets is the useful part and the only
 * part: it writes the shoot days. Call Sheet Create reads them back and
 * offers each one as a press-to-fill button, so the days are waiting by
 * the time anyone opens the tab.
 *
 * **Every day is its own row.** `project_shoots` has always been a table,
 * not three columns on the project — a build day, a tech day and four event
 * days is the normal shape of a job, and the calendar, the call sheets and
 * the client's dates block all read the rows.
 *
 * **A live stream's days are shoot days too (16 Oct 2026).** The day list
 * comes from `allDays`: the ticked shoot dates plus any days a live stream
 * runs. The crew turn up to a stream, it has a venue, and it belongs on the
 * client's schedule.
 *
 * Every write after the project row is **best effort and reported, never
 * fatal** — a page missing one of its five parts is a thirty-second fix; a
 * flow that throws away a minute of answers because a contact row failed is
 * not. The report says what to do about it.
 */

export interface SetupResult {
  ok: boolean
  error?: string
  slug?: string
  token?: string
}

const keyFor = (name: string, index: number) =>
  `${slugify(name) || 'piece'}-${index + 1}`

async function findProject(client: string, name: string) {
  if (!supabase) return null
  const { data } = await supabase
    .from('projects')
    .select('id, slug, access_token')
    .eq('client_name', client)
    .eq('project_name', name)
    .order('created_at', { ascending: false })
    .limit(1)
  return data && data[0] ? data[0] : null
}

/** Waits for the insert the context fired a moment ago — four tries, ~1s. */
async function waitForProject(client: string, name: string) {
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const row = await findProject(client, name)
    if (row) return row
    await new Promise((resolve) => setTimeout(resolve, 250))
  }
  return null
}

export async function applySetup(answers: SetupAnswers): Promise<SetupResult> {
  if (!supabase) return { ok: false, error: 'Supabase is not configured.' }

  const client = answers.client.trim()
  const name = answers.name.trim()
  const project = await waitForProject(client, name)
  if (!project) {
    return { ok: false, error: 'The project was created but could not be found to set up.' }
  }

  const id = String(project.id)
  const slug = String(project.slug ?? '')
  const token = String(project.access_token ?? '')
  const trouble: string[] = []

  const { error: projectError } = await supabase
    .from('projects')
    .update({
      page_blocks: blocksFromAnswers(answers),
      outline: answers.outline.trim() || null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)
  if (projectError) return { ok: false, error: `projects: ${projectError.message}`, slug, token }

  /* The pieces. Visible from the moment the page exists, so the client sees
     the slate of what is coming rather than a page that stops. A due date
     makes the tile read "Editing, due 16 November" instead of "Editing". */
  const pieces = piecesFrom(answers).filter((piece) => piece.name.trim())
  if (pieces.length > 0) {
    const { error } = await supabase.from('deliverables').insert(
      pieces.map((piece, index) => ({
        project_id: id,
        deliverable_key: keyFor(piece.name, index),
        title: piece.name.trim(),
        category: piece.category,
        status: 'Planned',
        visible_to_client: true,
        sort_order: index,
        due_date: piece.due || null,
        video_format: piece.format || null,
      })),
    )
    if (error) trouble.push(`the pieces (${error.message})`)
  }

  /* The days — shoot days and stream days alike. The database derives the
     project's next_shoot_* mirror from these rows, so nothing writes those
     directly. (ProjectFields.tsx used to, and silently lost the typing.)
     These rows are also what Call Sheet Create offers as press-to-fill
     days, which is the whole of this file's part in a call sheet. */
  const days = allDays(answers)
  if (days.length > 0) {
    const { error } = await supabase.from('project_shoots').insert(
      days.map((day, index) => ({
        project_id: id,
        start_date: day.date,
        end_date: day.end && day.end > day.date ? day.end : null,
        title: day.title.trim() || null,
        location: day.location.trim() || null,
        order_index: index,
      })),
    )
    if (error) trouble.push(`the shoot dates (${error.message})`)
  }

  /* The starter tracker. */
  if (answers.during.includes('plan')) {
    const steps = starterPlan(answers)
    const { error } = await supabase.from('project_steps').insert(
      steps.map((step, index) => ({
        project_id: id,
        title: step.title,
        owner: step.owner,
        owner_name: step.owner === 'client' ? client : null,
        status: 'not_started',
        phase: step.phase,
        start_date: step.start || null,
        order_index: index,
      })),
    )
    if (error) trouble.push(`the tracker (${error.message})`)
  }

  /* Who to email. Staff-only rows — see utils/clientTell.ts. */
  const contacts = answers.contacts.filter((person) => person.email.trim())
  if (contacts.length > 0) {
    const { error } = await supabase.from('project_client_contacts').insert(
      contacts.map((person) => ({
        project_id: id,
        name: person.name.trim(),
        email: person.email.trim().toLowerCase(),
      })),
    )
    if (error) trouble.push(`the contacts (${error.message})`)
  }

  if (trouble.length > 0) {
    return {
      ok: false,
      error: `The page is up, but ${trouble.join(' and ')} didn't save. Open it in the studio and add them.`,
      slug,
      token,
    }
  }
  return { ok: true, slug, token }
}
